import Link from "next/link";
import Image, { ImageProps } from "next/image";
import type { ReactNode } from "react";

const LOGO_SIZE = 72;

export default function Testimonial({
  href,
  logo,
  logoAlt,
  quote,
  name,
  credential,
}: {
  href: string;
  logo: ImageProps["src"];
  logoAlt: string;
  quote: ReactNode;
  name: string;
  credential: string;
}) {
  const shouldOpenInNewTab = !href?.startsWith("/");
  return (
    <section className="testimonial">
      <Link href={href}>
        <a
          className="padded-box featured-customer"
          target={shouldOpenInNewTab ? "_blank" : ""}
          rel={shouldOpenInNewTab ? "noopener noreferrer" : ""}
        >
          <Image
            src={logo}
            alt={logoAlt}
            width={LOGO_SIZE}
            height={LOGO_SIZE}
            objectFit="contain"
            objectPosition="left center"
            draggable="false"
            className="logo"
          />
          <blockquote>
            <p>{quote}</p>
            <cite>
              <span className="customer-name">
                {name}
                {", "}
              </span>
              <span className="customer-credential">{credential}</span>
              {href && <span className="read-more">Read more ›</span>}
            </cite>
          </blockquote>
        </a>
      </Link>
      <style jsx>{`
        .featured-customer {
          max-width: 640px;
          margin-top: ${LOGO_SIZE / 2}px;
          margin-inline: auto;
          border-radius: var(--module-radius);
          background-color: var(--module-color);
          display: flex;
          flex-direction: column;
          gap: var(--module-gap);
          text-decoration: none;
          color: inherit;
          transition: background-color 0.125s ease-in-out;
        }

        .featured-customer:hover,
        .featured-customer:focus {
          --module-color: var(--module-color-hover);
          --module-color-quote: var(--module-color-hover-quote);
        }

        .featured-customer > :global(span) {
          margin: -${LOGO_SIZE}px 0 -${LOGO_SIZE / 3}px !important;
        }

        @media screen and (min-width: 820px) {
          section {
            grid-column: -1 / 1;
          }
          .featured-customer {
            margin-top: ${LOGO_SIZE / 4}px;
            gap: 36px;
            flex-direction: row;
            align-items: center;
          }
          .featured-customer > :global(span) {
            margin: -${LOGO_SIZE}px 0 0 -${LOGO_SIZE * (2 / 3)}px !important;
            transform: scale(1.25);
          }
          blockquote p::before {
            left: -24px;
          }
          cite .read-more {
            margin-left: auto;
          }
        }

        blockquote {
          line-height: 1.5;
          margin: 0;
          position: relative;
          flex-grow: 1;
        }
        blockquote p {
          position: relative;
        }
        blockquote p::before,
        blockquote p::after {
          color: var(--module-color-quote);
          transition: color 0.125s ease-in-out;
          font-size: 48px;
          line-height: 16px;
          position: absolute;
        }
        blockquote p::before {
          content: "“";
          left: -20px;
          top: 12px;
        }
        blockquote p::after {
          content: "”";
          bottom: 0;
          padding-left: 4px;
        }

        cite {
          display: flex;
          align-items: center;
          color: #6d7384;
          font-style: normal;
          white-space: nowrap;
          flex-wrap: wrap;
        }
        cite .customer-name {
          position: relative;
          font-weight: bold;
          padding-right: 4px;
        }
        cite .customer-name::before {
          content: "—";
          position: absolute;
          left: -20px;
          color: var(--module-color-quote);
        }
        cite .customer-credential {
          flex-grow: 1;
          margin-right: 8px;
        }
        cite .read-more {
          color: #265cff;
          font-weight: bold;
        }
      `}</style>
    </section>
  );
}
